import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Phone, Globe, ChevronDown } from 'lucide-react';
import { Button } from './ui/button';
import { useLanguage } from '../context/LanguageContext';

const navigation = [
  { key: 'home', href: '/' },
  { key: 'about', href: '/about' },
  { key: 'services', href: '/services' },
  { key: 'solutions', href: '/solutions' },
  { key: 'process', href: '/process' },
  { key: 'consultants', href: '/consultants' },
  { key: 'contact', href: '/contact' },
];

const languages = [
  { code: 'en', name: 'English', flag: '🇬🇧' },
  { code: 'fr', name: 'Français', flag: '🇫🇷' },
  { code: 'es', name: 'Español', flag: '🇪🇸' },
];

export const Header = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [langMenuOpen, setLangMenuOpen] = useState(false);
  const location = useLocation();
  const { language, setLanguage, t } = useLanguage();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsOpen(false);
    setLangMenuOpen(false);
  }, [location]);

  const currentLang = languages.find(l => l.code === language);

  return (
    <header
      data-testid="header"
      className={`header-sticky transition-all duration-300 ${
        isScrolled ? 'shadow-sm' : ''
      }`}
    >
      <nav className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 mr-8" data-testid="logo-link">
            <img
              src="https://customer-assets.emergentagent.com/job_engineering-water/artifacts/z1g41hzx_logo%20ecrit%20magnum.jpg"
              alt="Magnum Fountain SL"
              className="h-12 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-6">
            {navigation.map((item) => (
              <Link
                key={item.key}
                to={item.href}
                data-testid={`nav-${item.key}`}
                className={`nav-link text-sm font-medium uppercase tracking-wider transition-colors ${
                  location.pathname === item.href
                    ? 'text-[#29ABE2]'
                    : 'text-[#1A1A1A] hover:text-[#29ABE2]'
                }`}
              >
                {t(`nav.${item.key}`)}
              </Link>
            ))}
          </div>

          {/* Right Side - Language + Phone + CTA */}
          <div className="hidden lg:flex items-center gap-4">
            {/* Language Selector */}
            <div className="relative">
              <button
                onClick={() => setLangMenuOpen(!langMenuOpen)}
                data-testid="language-selector"
                className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-[#6C757D] hover:text-[#29ABE2] transition-colors border border-gray-200 rounded-sm"
              >
                <span>{currentLang?.flag}</span>
                <span className="uppercase">{language}</span>
                <ChevronDown className={`w-4 h-4 transition-transform ${langMenuOpen ? 'rotate-180' : ''}`} />
              </button>
              
              <AnimatePresence>
                {langMenuOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="absolute top-full right-0 mt-2 bg-white border border-gray-200 shadow-lg rounded-sm overflow-hidden z-50"
                  >
                    {languages.map((lang) => (
                      <button
                        key={lang.code}
                        onClick={() => {
                          setLanguage(lang.code);
                          setLangMenuOpen(false);
                        }}
                        data-testid={`lang-${lang.code}`}
                        className={`flex items-center gap-3 w-full px-4 py-3 text-sm text-left hover:bg-[#F8F9FA] transition-colors ${
                          language === lang.code ? 'bg-[#F8F9FA] text-[#29ABE2]' : 'text-[#1A1A1A]'
                        }`}
                      >
                        <span>{lang.flag}</span>
                        <span>{lang.name}</span>
                      </button>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            <a
              href="tel:+34919933440"
              className="flex items-center gap-2 text-sm font-medium text-[#6C757D] hover:text-[#29ABE2] transition-colors"
              data-testid="header-phone"
            >
              <Phone className="w-4 h-4" />
              <span>+34 919 933 440</span>
            </a>
            <Link to="/contact">
              <Button
                data-testid="header-cta-btn"
                className="bg-[#1A1A1A] text-white hover:bg-[#29ABE2] rounded-none px-6 py-2 text-sm font-bold uppercase tracking-wider transition-colors duration-300"
              >
                {t('nav.getInTouch')}
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            data-testid="mobile-menu-btn"
            className="lg:hidden p-2"
            onClick={() => setIsOpen(!isOpen)}
          >
            {isOpen ? (
              <X className="w-6 h-6 text-[#1A1A1A]" />
            ) : (
              <Menu className="w-6 h-6 text-[#1A1A1A]" />
            )}
          </button>
        </div>
      </nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-white border-t border-gray-100"
            data-testid="mobile-menu"
          >
            <div className="px-6 py-4 space-y-4">
              {/* Mobile Language Selector */}
              <div className="flex gap-2 pb-4 border-b border-gray-100">
                {languages.map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => setLanguage(lang.code)}
                    className={`flex items-center gap-2 px-3 py-2 text-sm rounded-sm ${
                      language === lang.code
                        ? 'bg-[#29ABE2] text-white'
                        : 'bg-[#F8F9FA] text-[#1A1A1A]'
                    }`}
                  >
                    <span>{lang.flag}</span>
                    <span className="uppercase">{lang.code}</span>
                  </button>
                ))}
              </div>

              {navigation.map((item) => (
                <Link
                  key={item.key}
                  to={item.href}
                  data-testid={`mobile-nav-${item.key}`}
                  className={`block py-2 text-base font-medium uppercase tracking-wider ${
                    location.pathname === item.href
                      ? 'text-[#29ABE2]'
                      : 'text-[#1A1A1A]'
                  }`}
                >
                  {t(`nav.${item.key}`)}
                </Link>
              ))}
              <div className="pt-4 border-t border-gray-100">
                <a
                  href="tel:+34919933440"
                  className="flex items-center gap-2 py-2 text-[#29ABE2] font-medium"
                >
                  <Phone className="w-5 h-5" />
                  <span>+34 919 933 440</span>
                </a>
                <Link to="/contact" className="block mt-4">
                  <Button className="w-full bg-[#1A1A1A] text-white hover:bg-[#29ABE2] rounded-none py-3 text-sm font-bold uppercase tracking-wider">
                    {t('nav.getInTouch')}
                  </Button>
                </Link>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
